import { normalizeVideoUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useCartStore, CartItem } from "@/stores/cart-store";
import { useTruckStore } from "@/stores/truck-store";
import { useAddressStore } from "@/stores/address-store";
import { GradeBadge } from "./grade-badge";
import { Sparkles } from "lucide-react";
import { FACTORY_REFURBISHED_LABEL, FACTORY_REFURBISHED_GRADING_ANCHOR } from "@shared/palletDisplay";
import { CapacityBar } from "./capacity-indicator";
import { CartonizationDialog } from "./cartonization-dialog";
import { VideoPlayer } from "./video-player";
import { X, Trash2, Package, ChevronDown, ChevronUp, MapPin, Loader2, CheckCircle2, Truck, Video, Eye, Clock, AlertTriangle } from "lucide-react";
import { getShipDateColor } from "@/lib/shipDateColors";
import { useShipDateColorMap } from "@/hooks/useShipDateColorMap";
import { format, parseISO } from "date-fns";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "wouter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { formatMoney, multiplyMoney, sumMoney } from "@shared/currency";
import { trackViewCart } from "@/lib/analytics";
import { useGradeBadgeCategoryLookup } from "@/lib/category-meta";

interface GroupedItems {
  modelName: string;
  brand: string;
  grade: 'A' | 'B+' | 'B-';
  items: any[];
  totalQty: number;
  totalPrice: number;
  imageUrl?: string;
}

interface ModelGroup {
  modelId: string;
  modelName: string;
  brand: string;
  imageUrl?: string;
  gradeGroups: GroupedItems[];
  totalGrades: number;
  /** model.categoryId snapshot from the cart item — drives per-category
   *  grading copy + the pallet inventory pill (Task #156). */
  categoryId?: string | null;
}

interface UserAccount {
  id: string;
  companyName: string;
  isActive: boolean;
  salesTaxExemptUrl?: string | null;
  minimumOrderQuantity?: number;
}

interface AccountAddress {
  id: string;
  accountId: string;
  isBilling: boolean;
  isShipping: boolean;
  locationName: string;
  line1: string;
  line2: string | null;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  hasLoadingDock: boolean;
  hasForklift: boolean;
  isDefaultShipping: boolean;
}

interface FreightQuote {
  carrierId: string | null;
  carrierName: string;
  baseFreight: number;
  fuelSurcharge: number;
  fuelSurchargePercent: number;
  accessorialCharges: { code: string; name: string; amount: number }[];
  totalAccessorials: number;
  distanceSurcharge?: number;
  subtotal: number;
  totalCost: number;
  chargeableWeight: number;
  dimensionalWeight: number | null;
}

interface PickupLocation {
  id: string;
  locationName: string;
  line1: string;
  line2: string | null;
  city: string;
  region: string;
  postalCode: string;
  localPickupFee: number;
}

export function ShoppingCart() {
  const { items, removeItem, updateQuantity, clearCart, getTotalAmount, lastAddedGroupKey, highlightedModelId } = useCartStore();
  const { selectedTruckSize } = useTruckStore();
  const { user, isAuthenticated } = useAuth();
  const [removeItemId, setRemoveItemId] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCartonization, setShowCartonization] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const { selectedAddressId, setSelectedAddressId } = useAddressStore();
  const [freightQuote, setFreightQuote] = useState<FreightQuote | null>(null);
  const [freightQuoteLoading, setFreightQuoteLoading] = useState(false);
  const [freightQuoteError, setFreightQuoteError] = useState<string | null>(null);
  const [freightQuoteRetryCount, setFreightQuoteRetryCount] = useState(0);
  const [shippingMethod, setShippingMethod] = useState<'FREIGHT' | 'LOCAL_PICKUP' | null>(null);
  const [selectedPickupLocation, setSelectedPickupLocation] = useState<string | null>(null);
  const [showPickupDialog, setShowPickupDialog] = useState(false);
  const [shippingMethodInitialized, setShippingMethodInitialized] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideoItem, setSelectedVideoItem] = useState<{ serialNumber: string; videoUrl: string; grade: string } | null>(null);
  const [localQtyMap, setLocalQtyMap] = useState<Record<string, string>>({});
  // Per-category grade-pill resolver — drives the pallet inventory pill
  // and category-specific tooltip on cart line items (Task #156).
  const lookupGradeBadgeCategory = useGradeBadgeCategoryLookup();
  
  // Fetch user account for B2B users
  const { data: userAccount } = useQuery<UserAccount | null>({
    queryKey: ["/api/users/me/account"],
    enabled: isAuthenticated,
  });

  // Calculate freight total for tax calculation
  const freightTotal = freightQuote?.totalCost || 0;

  // Fetch cart data to get sales tax information and shipping method preference
  // Include freight amount in query to get accurate tax calculation
  interface CartDataResponse {
    salesTax: { taxRate: number; taxAmount: number } | null;
    shippingMethod: 'FREIGHT' | 'LOCAL_PICKUP' | null;
    selectedPickupWarehouseId: string | null;
  }
  const { data: cartData } = useQuery<CartDataResponse>({
    queryKey: freightTotal > 0 ? [`/api/cart?freightAmount=${freightTotal}`] : ["/api/cart"],
    enabled: isAuthenticated && items.length > 0,
  });

  // Initialize shipping method from server data when cart loads
  useEffect(() => {
    if (cartData && !shippingMethodInitialized) {
      if (cartData.shippingMethod) {
        setShippingMethod(cartData.shippingMethod);
      }
      if (cartData.selectedPickupWarehouseId) {
        setSelectedPickupLocation(cartData.selectedPickupWarehouseId);
      }
      setShippingMethodInitialized(true);
    }
  }, [cartData, shippingMethodInitialized]);

  // Save shipping method to server when it changes
  const updateShippingMethodOnServer = async (method: 'FREIGHT' | 'LOCAL_PICKUP' | null, pickupWarehouseId: string | null) => {
    try {
      await apiRequest('PATCH', '/api/cart', {
        shippingMethod: method,
        selectedPickupWarehouseId: pickupWarehouseId,
      });
    } catch (error) {
      console.error('Failed to save shipping method:', error);
    }
  };

  // Wrapper to update shipping method and persist to server
  const handleShippingMethodChange = (method: 'FREIGHT' | 'LOCAL_PICKUP') => {
    setShippingMethod(method);
    if (method === 'FREIGHT') {
      setSelectedPickupLocation(null);
      updateShippingMethodOnServer(method, null);
    } else {
      updateShippingMethodOnServer(method, selectedPickupLocation);
    }
  };

  // Wrapper to update pickup location and persist to server
  const handlePickupLocationChange = (locationId: string) => {
    setSelectedPickupLocation(locationId);
    updateShippingMethodOnServer('LOCAL_PICKUP', locationId);
  };

  const salesTax = cartData?.salesTax;
  const isB2BUser = !!userAccount;

  // Fetch account addresses for B2B users
  const { data: accountAddresses } = useQuery<AccountAddress[]>({
    queryKey: [`/api/accounts/${userAccount?.id}/addresses`],
    enabled: isB2BUser && !!userAccount?.id,
  });

  // Fetch pickup locations for B2B users
  const { data: pickupLocations } = useQuery<PickupLocation[]>({
    queryKey: ["/api/pickup-locations"],
    enabled: isB2BUser,
  });

  // Auto-select default shipping address for B2B users
  useEffect(() => {
    if (isB2BUser && accountAddresses && accountAddresses.length > 0 && !selectedAddressId) {
      const defaultAddress = accountAddresses.find(addr => addr.isDefaultShipping && addr.isShipping);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      } else {
        // If no default, select first shipping address
        const firstShipping = accountAddresses.find(addr => addr.isShipping);
        if (firstShipping) {
          setSelectedAddressId(firstShipping.id);
        }
      }
    }
  }, [isB2BUser, accountAddresses, selectedAddressId]);

  // Track view_cart event when cart is viewed with items
  const viewCartTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (items.length > 0) {
      const cartItemsKey = items.map(i => `${i.id}:${i.quantity}`).join('|');
      if (viewCartTrackedRef.current !== cartItemsKey) {
        viewCartTrackedRef.current = cartItemsKey;
        const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        trackViewCart(
          items.map(item => ({
            id: item.modelId,
            modelNumber: item.modelName,
            brand: item.brand,
            grade: item.grade,
            price: item.price,
            quantity: item.quantity,
          })),
          totalValue
        );
      }
    }
  }, [items]);

  // Helper function to fetch freight quote with retry logic
  const fetchFreightQuoteWithRetry = async (
    payload: any,
    maxRetries: number = 3
  ): Promise<FreightQuote> => {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const data = await apiRequest('POST', '/api/freight/quote-for-cart', payload);
        // The endpoint returns a bare array for non-admins and an
        // { quotes, freightDebug } object for admins — normalize to the array.
        return Array.isArray(data) ? data : (data?.quotes ?? data);
      } catch (error: any) {
        lastError = error;
        console.error(`Freight quote attempt ${attempt + 1}/${maxRetries} failed:`, error);
        
        // Don't retry on client errors (4xx) except 429 (rate limit)
        if (error?.status >= 400 && error?.status < 500 && error?.status !== 429) {
          throw error;
        }
        
        // Wait before retrying with exponential backoff (1s, 2s, 4s)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    throw lastError || new Error('Failed to get freight quote after multiple attempts');
  };

  // Manual retry handler for freight quotes
  const retryFreightQuote = () => {
    setFreightQuoteRetryCount(prev => prev + 1);
  };

  // Calculate freight quote for B2B users when address or cart changes
  useEffect(() => {
    const calculateFreightQuote = async () => {
      // Only calculate for B2B users with items in cart
      if (!isB2BUser || items.length === 0) {
        setFreightQuote(null);
        setFreightQuoteError(null);
        return;
      }

      // For freight shipping, need a selected address
      if (shippingMethod === 'FREIGHT') {
        if (!selectedAddressId) {
          setFreightQuote(null);
          setFreightQuoteError(null);
          return;
        }

        const address = accountAddresses?.find(addr => addr.id === selectedAddressId);
        if (!address) {
          setFreightQuote(null);
          setFreightQuoteError(null);
          return;
        }

        // Validate that both city AND state are provided. Server requires a
        // 2-letter state code; bail early when the saved address has a long
        // form like "Minnesota" instead of triggering the 3-retry loop on a
        // guaranteed 400.
        const regionCode = address.region?.trim().toUpperCase();
        if (!address.city || !regionCode || regionCode.length !== 2) {
          setFreightQuote(null);
          setFreightQuoteError(null);
          return;
        }

        setFreightQuoteLoading(true);
        setFreightQuoteError(null);

        try {
          const data = await fetchFreightQuoteWithRetry({
            cartItems: items.map(item => ({
              modelId: item.modelId,
              quantity: item.quantity,
            })),
            destinationState: address.region.toUpperCase(),
            destinationCity: address.city,
            destinationZip: address.postalCode,
            hasLoadingDock: address.hasLoadingDock,
            hasForklift: address.hasForklift,
          });

          setFreightQuote(data);
          setFreightQuoteError(null);
        } catch (error: any) {
          console.error("Error calculating freight quote:", error);
          setFreightQuote(null);
          const errorMessage = error?.message || 'Unable to calculate freight. Please try again.';
          setFreightQuoteError(errorMessage);
        } finally {
          setFreightQuoteLoading(false);
        }
      } else if (shippingMethod === 'LOCAL_PICKUP' && selectedPickupLocation) {
        // For local pickup, get a $0 quote to satisfy backend
        setFreightQuoteLoading(true);
        setFreightQuoteError(null);

        try {
          const data = await fetchFreightQuoteWithRetry({
            cartItems: items.map(item => ({
              modelId: item.modelId,
              quantity: item.quantity,
            })),
            shippingMethod: 'LOCAL_PICKUP',
            pickupWarehouseId: selectedPickupLocation,
          });

          setFreightQuote(data);
          setFreightQuoteError(null);
        } catch (error: any) {
          console.error("Error calculating pickup quote:", error);
          setFreightQuote(null);
          setFreightQuoteError('Unable to calculate pickup fee. Please try again.');
        } finally {
          setFreightQuoteLoading(false);
        }
      } else {
        setFreightQuote(null);
        setFreightQuoteError(null);
      }
    };

    calculateFreightQuote();
  }, [isB2BUser, shippingMethod, selectedAddressId, selectedPickupLocation, accountAddresses, items, freightQuoteRetryCount]);
  
  // Shared ship-date → color-slot map from /api/inventory/upcoming-ship-dates
  const cartShipDateColorMap = useShipDateColorMap();

  // Define grade order for sorting
  const gradeOrder = { 'A': 0, 'B+': 1, 'B-': 2 };
  
  // Group items by warehouse first, then by model, then by grade
  interface WarehouseGroup {
    warehouseCode: string;
    city: string;
    region: string;
    modelGroups: ModelGroup[];
  }

  const warehouseGroups: WarehouseGroup[] = useMemo(() => {
    // Group items by warehouse
    const warehouseMap = new Map<string, {
      warehouseCode: string;
      city: string;
      region: string;
      items: typeof items;
    }>();

    items.forEach(item => {
      const warehouseCode = item.warehouseCode || 'UNKNOWN';
      if (!warehouseMap.has(warehouseCode)) {
        warehouseMap.set(warehouseCode, {
          warehouseCode,
          city: item.city || 'Unknown',
          region: item.region || 'Unknown',
          items: [],
        });
      }
      warehouseMap.get(warehouseCode)!.items.push(item);
    });

    // For each warehouse, group by model then grade
    const warehouseGroupsArray: WarehouseGroup[] = [];
    
    warehouseMap.forEach(warehouse => {
      // First group by model+grade
      const gradeGroups: GroupedItems[] = warehouse.items.reduce((acc, item) => {
        const key = `${item.modelName}-${item.grade}`;
        const existingGroup = acc.find(g => `${g.modelName}-${g.grade}` === key);
        
        if (existingGroup) {
          existingGroup.items.push(item);
          existingGroup.totalQty += item.quantity;
          existingGroup.totalPrice += multiplyMoney(item.price, item.quantity); // Use multiplyMoney for cents precision
        } else {
          acc.push({
            modelName: item.modelName,
            brand: item.brand,
            grade: item.grade,
            items: [item],
            totalQty: item.quantity,
            totalPrice: multiplyMoney(item.price, item.quantity), // Use multiplyMoney for cents precision
            imageUrl: item.imageUrl,
          });
        }
        return acc;
      }, [] as GroupedItems[]);
      
      // Then group by model and sort grades
      const modelMap = new Map<string, ModelGroup>();
      gradeGroups.forEach(gradeGroup => {
        if (!modelMap.has(gradeGroup.modelName)) {
          modelMap.set(gradeGroup.modelName, {
            modelId: gradeGroup.items[0].modelId,
            modelName: gradeGroup.modelName,
            brand: gradeGroup.brand,
            imageUrl: gradeGroup.imageUrl,
            gradeGroups: [],
            totalGrades: 0,
            categoryId: (gradeGroup.items[0] as CartItem).categoryId ?? null,
          });
        }
        modelMap.get(gradeGroup.modelName)!.gradeGroups.push(gradeGroup);
      });
      
      // Sort grades within each model
      modelMap.forEach(modelGroup => {
        modelGroup.gradeGroups.sort((a, b) => gradeOrder[a.grade] - gradeOrder[b.grade]);
        modelGroup.totalGrades = modelGroup.gradeGroups.length;
      });
      
      warehouseGroupsArray.push({
        warehouseCode: warehouse.warehouseCode,
        city: warehouse.city,
        region: warehouse.region,
        modelGroups: Array.from(modelMap.values()),
      });
    });

    return warehouseGroupsArray;
  }, [items]);
  
  // Flatten model groups for key tracking (with warehouse context) - now at MODEL level, not grade level
  const allModelGroups = useMemo(() => 
    warehouseGroups.flatMap(w => 
      w.modelGroups.map(m => ({
        ...m,
        warehouseCode: w.warehouseCode
      }))
    ),
    [warehouseGroups]
  );
  
  // Track which keys we've seen before to distinguish new groups from user-collapsed groups
  const [seenKeys, setSeenKeys] = useState<Set<string>>(() => {
    return new Set(allModelGroups.map(m => `${m.warehouseCode}-${m.modelId}`));
  });
  
  // Initialize all models as expanded by default
  const [expandedModels, setExpandedModels] = useState<Set<string>>(() => {
    return new Set(allModelGroups.map(m => `${m.warehouseCode}-${m.modelId}`));
  });
  
  // Sync expandedModels when NEW models appear (but preserve user-collapsed state)
  useEffect(() => {
    const currentKeys = allModelGroups.map(m => `${m.warehouseCode}-${m.modelId}`);
    
    setSeenKeys(prev => {
      const newKeys = currentKeys.filter(key => !prev.has(key));
      const removedKeys = Array.from(prev).filter(key => !currentKeys.includes(key));
      
      if (newKeys.length === 0 && removedKeys.length === 0) {
        return prev; // No changes needed
      }
      
      const newSet = new Set(prev);
      
      // Add new keys
      if (newKeys.length > 0) {
        newKeys.forEach(key => newSet.add(key));
        // Expand only the first new model (exclusive accordion)
        setExpandedModels(new Set([newKeys[0]]));
      }
      
      // Remove keys that no longer exist
      removedKeys.forEach(key => newSet.delete(key));
      if (removedKeys.length > 0) {
        setExpandedModels(prevExpanded => {
          const newExpanded = new Set(prevExpanded);
          removedKeys.forEach(key => newExpanded.delete(key));
          return newExpanded;
        });
      }
      
      return newSet;
    });
  }, [allModelGroups]);
  
  // Smart cart collapsing: when new item added, expand that model and collapse others
  useEffect(() => {
    if (lastAddedGroupKey) {
      // Extract the model key from the group key (format: warehouseCode-modelName-grade)
      // We need to find which model this belongs to
      const parts = lastAddedGroupKey.split('-');
      if (parts.length >= 2) {
        const warehouseCode = parts[0];
        // Find the model that matches this group key
        const matchingModel = allModelGroups.find(m => 
          m.warehouseCode === warehouseCode && 
          m.gradeGroups.some(g => `${warehouseCode}-${g.modelName}-${g.grade}` === lastAddedGroupKey)
        );
        if (matchingModel) {
          setExpandedModels(new Set([`${warehouseCode}-${matchingModel.modelId}`]));
        }
      }
    }
  }, [lastAddedGroupKey, allModelGroups]);

  const toggleModel = (modelKey: string) => {
    setExpandedModels(prev => {
      if (prev.has(modelKey)) {
        // If clicking on an already-expanded model, collapse it
        return new Set();
      } else {
        // If clicking on a collapsed model, expand only this one (collapse all others)
        return new Set([modelKey]);
      }
    });
  };

  const handleRemoveItem = (itemId: string, skipConfirmation: boolean = false) => {
    if (skipConfirmation) {
      // Skip confirmation for B-grade serial items
      removeItem(itemId);
    } else {
      // Show confirmation for A-grade bulk items
      setRemoveItemId(itemId);
    }
  };

  const confirmRemoveItem = () => {
    if (removeItemId) {
      removeItem(removeItemId);
      setRemoveItemId(null);
    }
  };

  const handleClearCart = () => {
    setShowClearConfirm(true);
  };

  const confirmClearCart = () => {
    clearCart();
    setShowClearConfirm(false);
  };

  return (
    <>
      <Card className="sticky-cart border-0 shadow-none" data-testid="shopping-cart">
            {/* Capacity Indicator - HIDDEN FOR NOW */}
            {/* {selectedTruckSize && selectedTruckSize !== 'unknown' && (
              <CardContent className="p-3 border-b border-border space-y-2">
                <CapacityBar truckSize={selectedTruckSize} />
                {items.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full h-7 text-xs"
                    onClick={() => setShowCartonization(true)}
                    data-testid="button-view-truck-loading"
                  >
                    <Package className="mr-1 h-3 w-3" />
                    View Loading
                  </Button>
                )}
              </CardContent>
            )} */}

            {/* Cart Items */}
            <CardContent className="p-3 space-y-3 pl-[0px] pr-[0px] pt-[0px] pb-[0px]">
              {warehouseGroups.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Cart is empty
                </div>
              ) : (
                <>
                  {/* Warehouse grouping - Each in its own Card */}
                  {warehouseGroups.map((warehouse, warehouseIndex) => {
                    const warehouseTotal = warehouse.modelGroups.reduce((sum, m) => 
                      sum + m.gradeGroups.reduce((s, g) => s + g.totalPrice, 0), 0
                    );
                    const warehouseTotalUnits = warehouse.modelGroups.reduce((sum, m) =>
                      sum + m.gradeGroups.reduce((s, g) => s + g.totalQty, 0), 0
                    );

                    return (
                      <Card key={warehouse.warehouseCode} className="overflow-hidden" data-testid={`cart-warehouse-card-${warehouse.warehouseCode}`}>
                        {/* Warehouse Header - Green with stats - Mobile responsive */}
                        <CardHeader 
                          className="py-2 sm:py-2 px-3 sm:px-3"
                          style={{backgroundColor: '#9eba4a', color: '#000000'}}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm sm:text-xs">
                            <div className="flex items-center gap-2 sm:gap-2">
                              <MapPin className="h-4 w-4 sm:h-3 sm:w-3 flex-shrink-0" />
                              <span className="font-bold sm:font-semibold text-[222222] truncate">
                                {warehouse.warehouseCode} | {warehouse.city}, {warehouse.region}
                              </span>
                            </div>
                            <span className="sm:ml-auto font-semibold sm:font-medium">
                              {warehouseTotalUnits} units • {formatMoney(warehouseTotal)}
                            </span>
                          </div>
                        </CardHeader>
                        {/* Models within this warehouse — bucket-first when presale items are present */}
                        <CardContent className="p-2 sm:p-2">
                        {(() => {
                          // Warehouse-level presale date buckets
                          const allWarehouseItems: CartItem[] = warehouse.modelGroups.flatMap((m: ModelGroup) => m.gradeGroups.flatMap((g: GroupedItems) => g.items as CartItem[]));
                          const warehousePresaleDates = [...new Set(
                            allWarehouseItems.filter(i => i.expectedShipDate).map(i => i.expectedShipDate as string)
                          )].sort();
                          const warehouseHasPresale = warehousePresaleDates.length > 0;
                          const warehouseHasInStock = warehouseHasPresale && allWarehouseItems.some(i => !i.expectedShipDate);

                          // Filter warehouse model groups to items matching a bucket filter
                          const getBucketGroups = (itemFilter: (i: CartItem) => boolean): ModelGroup[] =>
                            warehouse.modelGroups.map((mg: ModelGroup) => {
                              const filteredGGs = mg.gradeGroups.map((gg: GroupedItems) => {
                                const filteredItems: CartItem[] = (gg.items as CartItem[]).filter(itemFilter);
                                if (!filteredItems.length) return null;
                                return {
                                  ...gg,
                                  items: filteredItems,
                                  totalQty: filteredItems.reduce((s: number, i: CartItem) => s + i.quantity, 0),
                                  totalPrice: filteredItems.reduce((s: number, i: CartItem) => s + i.price * i.quantity, 0),
                                };
                              }).filter((g): g is GroupedItems => g !== null);
                              if (!filteredGGs.length) return null;
                              return { ...mg, gradeGroups: filteredGGs };
                            }).filter((mg): mg is ModelGroup => mg !== null);

                          // Render a list of model group cards (date bucketing removed — handled at warehouse level)
                          const renderModelGroupList = (modelGroupsToRender: ModelGroup[]) =>
                            modelGroupsToRender.map((modelGroup: ModelGroup, mgIdx: number) => {
                              const description = modelGroup.gradeGroups.flatMap((g: GroupedItems) => g.items as CartItem[]).find(item => item.description)?.description;
                              const totalUnits = modelGroup.gradeGroups.reduce((sum: number, g: GroupedItems) => sum + g.totalQty, 0);
                              const modelTotal = modelGroup.gradeGroups.reduce((sum: number, g: GroupedItems) => sum + g.totalPrice, 0);
                              const modelKey = `${warehouse.warehouseCode}-${modelGroup.modelId}`;
                              const isModelExpanded = expandedModels.has(modelKey);
                              return (
                                <div
                                  key={`${modelGroup.modelId}-${mgIdx}`}
                                  className="border-b border-border p-2 sm:p-2 cursor-pointer hover:bg-secondary/20 transition-colors"
                                  onClick={() => toggleModel(modelKey)}
                                  role="button"
                                  aria-expanded={isModelExpanded}
                                  data-testid={`cart-model-toggle-${modelKey}`}
                                >
                                  <div className="mb-2 sm:mb-2">
                                    <div className="text-sm sm:text-sm text-foreground flex items-center gap-2 sm:gap-2 touch-target">
                                      {isModelExpanded ? <ChevronUp className="h-4 w-4 sm:h-4 sm:w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 sm:h-4 sm:w-4 flex-shrink-0" />}
                                      <span className="font-bold text-base sm:text-[16px] truncate">{modelGroup.brand} {modelGroup.modelName}</span>
                                    </div>
                                    {description && (
                                      <div className="text-xs sm:text-xs text-muted-foreground truncate ml-5 sm:ml-6">{description}</div>
                                    )}
                                  </div>
                                  <div className="flex gap-3 sm:gap-3">
                                    <Link href={`/product/${modelGroup.modelId}`} className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                      {modelGroup.imageUrl && (
                                        <div className="relative group">
                                          <img src={modelGroup.imageUrl} alt={`${modelGroup.brand} ${modelGroup.modelName}`} className="w-12 h-12 sm:w-12 sm:h-12 object-contain rounded border border-border group-hover:opacity-60 transition-opacity cursor-pointer" />
                                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <Eye className="h-5 w-5 text-foreground" />
                                          </div>
                                        </div>
                                      )}
                                    </Link>
                                    <div className="flex items-start justify-center flex-shrink-0 pt-2 sm:pt-3">
                                      <span className="text-2xl sm:text-2xl font-bold text-foreground">{totalUnits}</span>
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                      {modelGroup.gradeGroups.map((gradeGroup: GroupedItems) => {
                                        const groupKey = `${warehouse.warehouseCode}-${gradeGroup.modelName}-${gradeGroup.grade}`;
                                        const items = gradeGroup.items as CartItem[];
                                        const unitPrice = items[0]?.price || 0;
                                        return (
                                          <div key={groupKey} className="space-y-0">
                                            <div className="p-1 sm:p-1 flex items-center gap-2 sm:gap-2 text-sm sm:text-xs" data-testid={`cart-group-${groupKey}`}>
                                              {items[0]?.isFactoryRefurbished ? (
                                                <Link
                                                  href={FACTORY_REFURBISHED_GRADING_ANCHOR}
                                                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border-2 border-emerald-500 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 transition-colors cursor-pointer"
                                                  data-testid={`cart-badge-factory-refurbished-${groupKey}`}
                                                  title="Factory-refurbished by the manufacturer to like-new spec — click to learn more"
                                                  onClick={() => onClose && onClose()}
                                                >
                                                  <Sparkles className="h-3 w-3" />
                                                  {FACTORY_REFURBISHED_LABEL}
                                                </Link>
                                              ) : (
                                                <GradeBadge grade={gradeGroup.grade} price={unitPrice} className="text-xs sm:text-xs" compact={false} category={lookupGradeBadgeCategory(modelGroup.categoryId)} />
                                              )}
                                              <span className="text-muted-foreground font-semibold sm:font-medium text-base sm:text-sm">{gradeGroup.totalQty}</span>
                                              {(() => {
                                                const todayYmd = new Date().toISOString().slice(0, 10);
                                                const dates = Array.from(new Set(items.map(i => i.expectedShipDate).filter((d): d is string => !!d && d > todayYmd)));
                                                if (dates.length === 0) return null;
                                                if (dates.length === 1) {
                                                  const slot = getShipDateColor(dates[0], cartShipDateColorMap);
                                                  return (
                                                    <span
                                                      className={`inline-flex items-center gap-1 text-[10px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded ${slot.bannerBg} ${slot.text}`}
                                                      data-testid={`cart-ship-date-${groupKey}`}
                                                    >
                                                      <Clock className="h-2.5 w-2.5" />
                                                      Ships {format(parseISO(dates[0]), 'MMM d')}
                                                    </span>
                                                  );
                                                }
                                                return (
                                                  <span
                                                    className="inline-flex items-center gap-1 text-[10px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500 text-white"
                                                    title={dates.map(d => format(parseISO(d), 'MMM d, yyyy')).join(' / ')}
                                                    data-testid={`cart-ship-date-mixed-${groupKey}`}
                                                  >
                                                    <Clock className="h-2.5 w-2.5" />
                                                    Multiple dates
                                                  </span>
                                                );
                                              })()}
                                              <span className="font-bold sm:font-semibold text-foreground ml-auto text-base sm:text-sm">{formatMoney(gradeGroup.totalPrice)}</span>
                                            </div>
                                            {!isModelExpanded && items[0]?.serialNumber && items.length > 0 && (
                                              <div className="px-2 sm:px-2 pb-1 sm:pb-1 text-xs sm:text-[10px] text-muted-foreground/50 italic truncate">
                                                {items.slice(0, 2).map(item => item.serialNumber).join(', ')}
                                                {items.length > 2 && ` +${items.length - 2} more`}
                                              </div>
                                            )}
                                            {isModelExpanded && (
                                              <div className="bg-secondary/30 p-2 sm:p-2 rounded" id={`cart-group-content-${groupKey}`} role="region">
                                                {items[0]?.serialNumber ? (
                                                  // B-grade serials — rendered directly; date bucketing is at warehouse level
                                                  <div className="grid grid-cols-1 gap-1 sm:gap-1">
                                                    {[...items].reverse().map((item: CartItem) => (
                                                      <div key={item.id} className="group px-2 sm:px-2 py-1 sm:py-0.5 flex items-center justify-between text-sm sm:text-xs hover:bg-secondary/50 rounded gap-2" data-testid={`cart-item-${item.id}`}>
                                                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                          {item.videoUrl ? (
                                                            <button
                                                              className="text-muted-foreground text-sm sm:text-xs flex items-center gap-2 hover:text-primary cursor-pointer text-left touch-target truncate"
                                                              onClick={(e) => { e.stopPropagation(); setSelectedVideoItem({ serialNumber: item.serialNumber, videoUrl: normalizeVideoUrl(item.videoUrl) || '', grade: item.grade }); setIsVideoModalOpen(true); }}
                                                              data-testid={`button-play-video-${item.id}`}
                                                            >
                                                              <Video className="h-5 w-5 sm:h-3 sm:w-3 text-primary flex-shrink-0" />
                                                              <span className="truncate">{item.serialNumber}</span>
                                                            </button>
                                                          ) : (
                                                            <span className="text-muted-foreground text-sm sm:text-xs truncate">{item.serialNumber}</span>
                                                          )}
                                                        </div>
                                                        <Button variant="ghost" size="sm" aria-label="Remove item" className="h-7 w-7 sm:h-4 sm:w-4 p-0 flex-shrink-0 text-red-500 hover:text-red-600 hover:bg-transparent touch-target" onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id, true); }} data-testid={`button-remove-${item.id}`}>
                                                          <Trash2 className="h-4 w-4 sm:h-3 sm:w-3" />
                                                        </Button>
                                                      </div>
                                                    ))}
                                                  </div>
                                                ) : (
                                                  // A-grade items — quantity controls
                                                  <div className="space-y-1">
                                                    {items.map((item: CartItem) => (
                                                      <div key={item.id} className="flex items-center justify-center text-sm sm:text-xs" data-testid={`cart-item-${item.id}`}>
                                                        <div className="flex items-center gap-2 sm:gap-1.5">
                                                          <Button variant="outline" size="sm" aria-label="Decrease quantity" className="h-8 w-8 sm:h-7 sm:w-7 p-0 touch-target" onClick={(e) => { e.stopPropagation(); const step = item.orderMultiple || 1; const minQty = item.minOrderQty || 1; const nextQty = item.quantity - step; if (nextQty < minQty) { handleRemoveItem(item.id, false); } else { updateQuantity(item.id, nextQty); } }} data-testid={`button-decrease-qty-${item.id}`}>
                                                            {item.quantity <= (item.minOrderQty || 1) ? <X className="icon-x h-4 w-4 sm:h-4 sm:w-4" /> : <span className="text-base sm:text-base font-semibold">−</span>}
                                                          </Button>
                                                          <input type="number" value={localQtyMap[item.id] ?? String(item.quantity)} onChange={(e) => { e.stopPropagation(); setLocalQtyMap(m => ({ ...m, [item.id]: e.target.value })); }} onBlur={(e) => { e.stopPropagation(); setLocalQtyMap(m => { const n = { ...m }; delete n[item.id]; return n; }); const step = item.orderMultiple || 1; const minQty = item.minOrderQty || 1; const raw = parseInt(e.target.value) || 0; if (raw <= 0) { handleRemoveItem(item.id, false); return; } let snapped = step > 1 ? Math.ceil(raw / step) * step : raw; snapped = Math.max(snapped, minQty); if (item.maxQuantity !== undefined) snapped = Math.min(snapped, item.maxQuantity); updateQuantity(item.id, snapped); }} onClick={(e) => e.stopPropagation()} className="w-12 sm:w-14 h-8 sm:h-7 px-2 sm:px-2 text-center border border-border rounded bg-background text-base sm:text-base" min={item.minOrderQty || 1} step={item.orderMultiple || 1} data-testid={`input-qty-${item.id}`} />
                                                          <Button variant="outline" size="sm" aria-label="Increase quantity" className="h-8 w-8 sm:h-7 sm:w-7 p-0 touch-target" onClick={(e) => { e.stopPropagation(); const step = item.orderMultiple || 1; updateQuantity(item.id, item.quantity + step); }} disabled={item.maxQuantity !== undefined && item.quantity >= item.maxQuantity} title={item.maxQuantity !== undefined && item.quantity >= item.maxQuantity ? `Max available: ${item.maxQuantity}` : undefined} data-testid={`button-increase-qty-${item.id}`}>
                                                            <span className="text-sm sm:text-base font-semibold">+</span>
                                                          </Button>
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      <div className="text-sm sm:text-sm text-foreground font-bold sm:font-semibold mt-1 text-right">
                                        Subtotal: {formatMoney(modelTotal)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            });

                          // No presale items — render all model groups as before
                          if (!warehouseHasPresale) {
                            return renderModelGroupList(warehouse.modelGroups);
                          }

                          // Bucket-first: Available now section + per-date Arriving sections
                          return (
                            <>
                              {warehouseHasInStock && (
                                <div className="mb-1">
                                  <div className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-800 bg-green-100 dark:text-green-200 dark:bg-green-900/40 rounded mb-1">
                                    <CheckCircle2 className="w-3 h-3 shrink-0" />
                                    <span>Available now</span>
                                  </div>
                                  {renderModelGroupList(getBucketGroups(i => !i.expectedShipDate))}
                                </div>
                              )}
                              {warehousePresaleDates.map((date: string) => {
                                const colorSlot = getShipDateColor(date, cartShipDateColorMap);
                                const label = format(parseISO(date), 'MMM d, yyyy');
                                return (
                                  <div key={date} className="mb-1">
                                    <div className={`flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded mb-1 ${colorSlot.bannerBg}`}>
                                      <Clock className="w-3 h-3 shrink-0" />
                                      <span>Arriving {label}</span>
                                    </div>
                                    {renderModelGroupList(getBucketGroups(i => i.expectedShipDate === date))}
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </CardContent>

            {/* Shipping Method Selector for B2B Users */}
            {isB2BUser && warehouseGroups.length > 0 && (
              <CardContent className="p-3 sm:p-4 border-b border-border">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Truck className={`h-4 w-4 ${!shippingMethod ? 'text-[#c41e3a]' : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-semibold ${!shippingMethod ? 'text-[#c41e3a]' : 'text-foreground'}`}>
                      {!shippingMethod ? 'Select Shipping Method' : 'Shipping Method'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={shippingMethod === 'FREIGHT' ? 'default' : 'outline'}
                      size="sm"
                      className={`flex-1 h-9 text-xs ${!shippingMethod && shippingMethod !== 'FREIGHT' ? 'border-[#c41e3a] text-[#c41e3a] hover:bg-[#c41e3a]/10' : ''}`}
                      onClick={() => handleShippingMethodChange('FREIGHT')}
                      data-testid="button-shipping-freight"
                    >
                      <Truck className="mr-1 h-3.5 w-3.5" />
                      Freight
                    </Button>
                    {pickupLocations && pickupLocations.length > 0 && (
                      <Button
                        variant={shippingMethod === 'LOCAL_PICKUP' ? 'default' : 'outline'}
                        size="sm"
                        className={`flex-1 h-9 text-xs ${!shippingMethod && shippingMethod !== 'LOCAL_PICKUP' ? 'border-[#c41e3a] text-[#c41e3a] hover:bg-[#c41e3a]/10' : ''}`}
                        onClick={() => {
                          handleShippingMethodChange('LOCAL_PICKUP');
                          if (!selectedPickupLocation) {
                            setShowPickupDialog(true);
                          }
                        }}
                        data-testid="button-shipping-pickup"
                      >
                        <MapPin className="mr-1 h-3.5 w-3.5" />
                        Local Pickup
                      </Button>
                    )}
                  </div>
                  {/* Show selected shipping info */}
                  {shippingMethod === 'LOCAL_PICKUP' && selectedPickupLocation && (() => {
                    const location = pickupLocations?.find(l => l.id === selectedPickupLocation);
                    if (location) {
                      return (
                        <div 
                          className="text-xs text-[#c41e3a] font-medium flex items-center gap-1 cursor-pointer hover:underline"
                          onClick={() => setShowPickupDialog(true)}
                          data-testid="shipping-pickup-location"
                        >
                          <MapPin className="h-3 w-3" />
                          Pickup in {location.city}, {location.region}
                          {location.localPickupFee > 0 && ` (+${formatMoney(location.localPickupFee)})`}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {shippingMethod === 'LOCAL_PICKUP' && !selectedPickupLocation && (
                    <div 
                      className="text-xs text-[#c41e3a] font-medium flex items-center gap-1 cursor-pointer hover:underline"
                      onClick={() => setShowPickupDialog(true)}
                      data-testid="shipping-select-pickup"
                    >
                      <MapPin className="h-3 w-3" />
                      Select pickup location
                    </div>
                  )}
                </div>
              </CardContent>
            )}

            {/* Subtotal - Simplified for cart sidebar */}
            {warehouseGroups.length > 0 && (
              <>
                <CardContent className="p-3 sm:p-4 border-b border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-xl sm:text-lg font-bold text-foreground">Subtotal</span>
                    <span className="text-2xl sm:text-xl font-bold text-primary" data-testid="cart-total">
                      {formatMoney(getTotalAmount())}
                    </span>
                  </div>
                </CardContent>

                {/* Minimum order quantity warning for B2B accounts */}
                {isB2BUser && userAccount?.minimumOrderQuantity && (() => {
                  const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
                  const minQty = userAccount.minimumOrderQuantity!;
                  if (totalQty < minQty) {
                    return (
                      <CardContent className="p-2 pb-0">
                        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600 px-3 py-2 text-sm text-amber-800 dark:text-amber-200" data-testid="alert-minimum-order">
                          <span className="font-semibold shrink-0">⚠</span>
                          <span>Minimum order is <strong>{minQty} units</strong> — you have <strong>{totalQty}</strong>. Add {minQty - totalQty} more to proceed.</span>
                        </div>
                      </CardContent>
                    );
                  }
                  return null;
                })()}

                {/* Cart Actions */}
                <CardContent className="p-2 space-y-1">
                  {user && (user as any).isActive === false ? (
                    <>
                      <div
                        className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-800 dark:text-red-200 mb-2"
                        data-testid="alert-account-inactive"
                      >
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                          Your account is locked. You can keep items in your cart, but you can't check out until it's reactivated. Please contact us for help.
                        </span>
                      </div>
                      <Button
                        disabled
                        className="w-full bg-muted text-muted-foreground pt-[20px] pb-[20px] text-[16px] font-bold cursor-not-allowed"
                        data-testid="button-checkout-disabled"
                      >
                        Account inactive
                      </Button>
                    </>
                  ) : (
                    <Link href="/checkout">
                      <Button
                        className="w-full bg-primary text-[#222222] hover:bg-primary/90 pt-[20px] pb-[20px] text-[16px] font-bold"
                        data-testid="button-checkout"
                      >
                        Checkout
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full h-7 text-xs font-bold text-[#c41e3a] hover:bg-[#c41e3a]/10 hover:text-[#c41e3a] hover:font-bold border-[#c41e3a]"
                    onClick={handleClearCart}
                    data-testid="button-clear-cart"
                  >
                    Clear Cart
                  </Button>
                </CardContent>
              </>
            )}
        </Card>
      {/* Remove Item Confirmation Dialog */}
      <AlertDialog open={!!removeItemId} onOpenChange={() => setRemoveItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Item?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this item from your cart?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-remove">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRemoveItem}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-remove"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Clear Cart Confirmation Dialog */}
      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Cart?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove all items from your cart? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-clear">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmClearCart}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-clear"
            >
              Clear Cart
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Cartonization Dialog */}
      <CartonizationDialog 
        open={showCartonization} 
        onOpenChange={setShowCartonization}
      />
      {/* Address Selector Dialog */}
      <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Shipping Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {accountAddresses?.filter(addr => addr.isShipping).map((address) => (
              <div
                key={address.id}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedAddressId === address.id 
                    ? 'border-2 border-primary bg-primary/5' 
                    : 'border border-border hover:border-primary/50 bg-secondary/20'
                }`}
                onClick={() => {
                  setSelectedAddressId(address.id);
                  setShowAddressDialog(false);
                }}
                data-testid={`address-option-${address.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <p className="font-semibold text-sm">{address.locationName}</p>
                      {address.isDefaultShipping && (
                        <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">Default</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5 ml-6">
                      <p>{address.line1}</p>
                      {address.line2 && <p>{address.line2}</p>}
                      <p>{address.city}, {address.region} {address.postalCode}</p>
                    </div>
                    {(address.hasLoadingDock || address.hasForklift) && (
                      <div className="flex gap-1 mt-2 ml-6">
                        {address.hasLoadingDock && (
                          <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded border border-border">
                            Dock
                          </span>
                        )}
                        {address.hasForklift && (
                          <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded border border-border">
                            Forklift
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Pickup Location Selector Dialog */}
      <Dialog open={showPickupDialog} onOpenChange={setShowPickupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Pickup Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {pickupLocations?.map((location) => (
              <div
                key={location.id}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedPickupLocation === location.id 
                    ? 'border-2 border-primary bg-primary/5' 
                    : 'border border-border hover:border-primary/50 bg-secondary/20'
                }`}
                onClick={() => {
                  handlePickupLocationChange(location.id);
                  setShowPickupDialog(false);
                }}
                data-testid={`pickup-location-option-${location.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <p className="font-semibold text-sm">{location.locationName}</p>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5 ml-6">
                      <p>{location.line1}</p>
                      {location.line2 && <p>{location.line2}</p>}
                      <p>{location.city}, {location.region} {location.postalCode}</p>
                    </div>
                    {location.localPickupFee > 0 && (
                      <div className="mt-2 ml-6">
                        <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded border border-border">
                          Pickup Fee: {formatMoney(location.localPickupFee)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Video Modal for Serialized units */}
      <Dialog open={isVideoModalOpen} onOpenChange={(open) => {
        setIsVideoModalOpen(open);
        if (!open) setSelectedVideoItem(null);
      }}>
        <DialogContent className="max-w-4xl" data-testid="cart-video-modal">
          <DialogHeader>
            <DialogTitle>
              {selectedVideoItem?.serialNumber} - {selectedVideoItem?.grade} Grade
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <VideoPlayer
              src={selectedVideoItem?.videoUrl || ''}
              className="w-full aspect-video"
              autoPlay={true}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
